import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';

const service = axios.create({
	baseURL: API_BASE_URL,
	timeout: 60000
})

const AUTH_HEADER = 'Authorization'

// Backend error handling already flattens field errors into `message` (see
// apps.core.exceptions.api_exception_handler); this is a safety net for any
// response that still only carries the raw `errors` map.
const firstFieldError = (errors) => {
	if (!errors) return null
	if (typeof errors === 'string') return errors
	if (Array.isArray(errors)) {
		for (const item of errors) {
			const found = firstFieldError(item)
			if (found) return found
		}
		return null
	}
	if (typeof errors === 'object') {
		for (const value of Object.values(errors)) {
			const found = firstFieldError(value)
			if (found) return found
		}
	}
	return null
}

// --- Request interceptor: attach the access token -------------------------
service.interceptors.request.use(config => {
	const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;
	if (jwtToken) {
		config.headers[AUTH_HEADER] = `Bearer ${jwtToken}`
	}
	return config
}, error => {
	notification.error({ message: 'Error' })
	return Promise.reject(error)
})

// --- Refresh handling ----------------------------------------------------
let isRefreshing = false
let queue = []

const processQueue = (error, token = null) => {
	queue.forEach(p => (error ? p.reject(error) : p.resolve(token)))
	queue = []
}

const forceSignOut = () => {
	localStorage.removeItem(AUTH_TOKEN)
	localStorage.removeItem(REFRESH_TOKEN)
	store.dispatch(signOutSuccess())
}

// --- Response interceptor ----------------------------------------------
service.interceptors.response.use((response) => response.data, async (error) => {
	const originalRequest = error.config
	const status = error.response?.status
	const isAuthCall = originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/refresh')

	// DRF's NotAuthenticated ("Authentication credentials were not provided.") means the
	// request went out with no Authorization header at all — distinct from an expired/invalid
	// token (AuthenticationFailed/InvalidToken), which the refresh flow below handles. If we
	// already have a token in localStorage, this is a client-side timing hiccup (e.g. a request
	// that was in flight the instant a sign-in/sign-out swapped the token) rather than a real
	// auth failure, so retry once with the current token before bothering the user.
	const noTokenSent = error.response?.data?.errors?.detail === 'Authentication credentials were not provided.'
	if (status === 401 && noTokenSent && !originalRequest._authHeaderRetry) {
		const currentToken = localStorage.getItem(AUTH_TOKEN)
		if (currentToken) {
			originalRequest._authHeaderRetry = true
			originalRequest.headers[AUTH_HEADER] = `Bearer ${currentToken}`
			return service(originalRequest)
		}
	}

	if (status === 401 && !originalRequest._retry && !isAuthCall) {
		const refreshToken = localStorage.getItem(REFRESH_TOKEN)
		if (!refreshToken) {
			forceSignOut()
			return Promise.reject(error)
		}
		if (isRefreshing) {
			return new Promise((resolve, reject) => queue.push({ resolve, reject }))
				.then(token => {
					originalRequest.headers[AUTH_HEADER] = `Bearer ${token}`
					return service(originalRequest)
				})
		}
		originalRequest._retry = true
		isRefreshing = true
		try {
			const resp = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh: refreshToken })
			const newAccess = resp.data.access
			localStorage.setItem(AUTH_TOKEN, newAccess)
			if (resp.data.refresh) {
				localStorage.setItem(REFRESH_TOKEN, resp.data.refresh)
			}
			processQueue(null, newAccess)
			originalRequest.headers[AUTH_HEADER] = `Bearer ${newAccess}`
			return service(originalRequest)
		} catch (refreshError) {
			processQueue(refreshError, null)
			forceSignOut()
			return Promise.reject(refreshError)
		} finally {
			isRefreshing = false
		}
	}

	const notificationParam = { message: '' }
	const data = error.response?.data
	const backendMessage = (data?.message && data.message !== 'Request failed.' ? data.message : null)
		|| (typeof data?.detail === 'string' ? data.detail : null)
		|| firstFieldError(data?.errors)
		|| data?.message

	if (status === 401 || status === 403) {
		notificationParam.message = backendMessage || 'Authentication failed'
	} else if (status === 404) {
		notificationParam.message = backendMessage || 'Not found'
	} else if (status === 400) {
		notificationParam.message = backendMessage || 'Please check the form and try again'
	} else if (status === 500) {
		notificationParam.message = 'Internal server error'
	} else if (!error.response) {
		notificationParam.message = 'Network error — is the API running?'
	} else {
		notificationParam.message = backendMessage || 'Request failed'
	}

	if (notificationParam.message) {
		notification.error(notificationParam)
	}
	return Promise.reject(error);
});

export default service
